import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageSquare, Share2, MoreHorizontal, Plus, Trash2, Edit } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import Modal from '../../components/Modal';

export default function StoreReels() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reels, setReels] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reelToDelete, setReelToDelete] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    productId: '',
    tags: ''
  });

  // Fetch store's reels and products
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch reels for the current store
        if (user?.storeId) {
          const reelsResponse = await api.get(`/reels?store=${user.storeId._id}`);
          setReels(reelsResponse.data.data.docs || []);
          
          // Fetch products for the current store
          const productsResponse = await api.get(`/products?store=${user.storeId._id}`);
          setProducts(productsResponse.data.data.products || []);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Handle video file selection
  const handleVideoChange = (e) => {
  const file = e.target.files[0];
  if (file) {
    if (file.size > 50 * 1024 * 1024) { // 50MB limit
      setError('Video file size exceeds 50MB limit');
      return;
    }
    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
    setError('');
  }
};

  // Handle form input changes
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Create a new reel
  const handleCreateReel = async () => {
  try {
    setLoading(true);
    
    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('description', formData.description);
    
    if (formData.productId) {
      formDataToSend.append('productId', formData.productId);
    }
    
    if (formData.tags) {
      formDataToSend.append('tags', formData.tags);
    }
    
    // Ensure videoFile exists before appending
    if (videoFile) {
      formDataToSend.append('video', videoFile);
    } else {
      throw new Error('Video file is required');
    }

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    };

    const { data } = await api.post('/reels', formDataToSend, config);

    if (data.success) {
      setReels([data.data.reel, ...reels]);
      setShowCreateModal(false);
      resetForm();
    }
  } catch (err) {
    console.error('Error creating reel:', err);
    setError(err.response?.data?.message || err.message || 'Failed to create reel');
  } finally {
    setLoading(false);
  }
};

  // Delete a reel
  const handleDeleteReel = async () => {
    try {
      setLoading(true);
      const { data } = await api.delete(`/reels/${reelToDelete}`);
      
      if (data.success) {
        setReels(reels.filter(reel => reel._id !== reelToDelete));
        setShowDeleteModal(false);
      }
    } catch (err) {
      console.error('Error deleting reel:', err);
      setError('Failed to delete reel');
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      productId: '',
      tags: ''
    });
    setVideoFile(null);
    setVideoPreview('');
  };

  if (loading && !reels.length) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="p-4 text-red-500 text-center">
        {error}
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Your Reels</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 transition-colors"
        >
          <Plus size={18} className="mr-2" />
          Create Reel
        </button>
      </div>

      {reels.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto h-24 w-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg 
              className="w-12 h-12 text-gray-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="1.5" 
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900">No reels yet</h3>
          <p className="mt-1 text-gray-500">Create your first reel to showcase your products</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 transition-colors"
          >
            Create Reel
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reels.map(reel => (
            <div key={reel._id} className="border rounded-lg overflow-hidden">
              <div className="relative pt-[177.78%] bg-black">
                <video
                  src={reel.video.url}
                  className="absolute inset-0 w-full h-full object-cover"
                  controls
                />
              </div>
              <div className="p-4">
                <h3 className="font-medium text-gray-900 mb-1">{reel.title}</h3>
                {reel.product && (
                  <p className="text-sm text-gray-600 mb-2">
                    Product: {reel.product.name}
                  </p>
                )}
                <p className="text-sm text-gray-500 line-clamp-2 mb-3">{reel.description}</p>
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <div className="flex space-x-4">
                    <span className="flex items-center">
                      <Heart size={16} className="mr-1" />
                      {reel.likes.length}
                    </span>
                    <span className="flex items-center">
                      <MessageSquare size={16} className="mr-1" />
                      {reel.comments.length}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => navigate(`/reels/${reel._id}`)}
                      className="text-pink-600 hover:text-pink-800"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => {
                        setReelToDelete(reel._id);
                        setShowDeleteModal(true);
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Reel Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="Create New Reel"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product (optional)
            </label>
            <select
              name="productId"
              value={formData.productId}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              disabled={loading || products.length === 0}
            >
              <option value="">Select a product</option>
              {products.map(product => (
                <option key={product._id} value={product._id}>
                  {product.name}
                </option>
              ))}
            </select>
            {products.length === 0 && !loading && (
              <p className="mt-1 text-sm text-gray-500">No products found in your store</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags (comma separated)
            </label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              placeholder="fashion, summer, sale"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Video
            </label>
            {videoPreview ? (
              <div className="relative">
                <video
                  src={videoPreview}
                  className="w-full rounded-md"
                  controls
                />
                <button
                  type="button"
                  onClick={() => {
                    setVideoFile(null);
                    setVideoPreview('');
                  }}
                  className="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded-full"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                    </svg>
                    <p className="mb-2 text-sm text-gray-500">Upload video (MP4, max 50MB)</p>
                  </div>
                  <input 
                    id="video-upload" 
                    type="file" 
                    className="hidden" 
                    accept="video/mp4,video/quicktime"
                    onChange={handleVideoChange}
                    required
                  />
                </label>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreateReel}
              disabled={loading || !videoFile}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Reel'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Reel"
      >
        <p className="text-gray-600 mb-6">Are you sure you want to delete this reel? This action cannot be undone.</p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setShowDeleteModal(false)}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteReel}
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
          >
            {loading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </Modal>
    </div>
  );
}